import { createDebugLogger } from "./debug.js";

const debugUtils = createDebugLogger("RAD_DEBUG_UTILS");

const mostDistantSubthreadX = (refinement, subthreads) => {
  const farX = subthreads.reduce(
    (acc, curr) => {
      if (curr.position.x > acc.position.x && !curr.poststate_id) {
        return curr;
      }
      return acc;
    },
    { position: { x: refinement.position.x } }
  );
  return farX.position.x;
};

export const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

export const getRADId = (rad) => rad.id;
export const getRADName = (rad) => rad.info?.name;

export const getRoleThreads = (rad) => (role_id) =>
  (rad.threads ?? []).filter((t) => t?.role_id === role_id);

export const getThreadActivities = (rad) => (thread_id) => {
  // Made this internal component as it's a little dangerous
  // if used directly due to sequencing issues.
  return (rad.activities ?? []).filter((a) => a.thread_id === thread_id);
};

export const getFarY = (rad) => (thread) => {
  if (["case", "part", "repeat"].includes(thread.class)) {
    const refinement = rad.activities.find((a) => a.id === thread.parent_id);
    if (
      refinement.poststate_id === thread.poststate_id ||
      !thread.poststate_id
    ) {
      return refinement.size.height;
    }
    return null; // non-return subthread
  }
  return null; // threads and repeats
};

export const getThreadCaseRefinements = (rad) => (thread_id) =>
  (rad.activities ?? [])
    .filter(
      (refinement) =>
        refinement.thread_id === thread_id && refinement.type === "case"
    )
    .map((refinement) => ({
      ...refinement,
      furthestX: mostDistantSubthreadX(
        refinement,
        getSubthreads(rad)(refinement.id)
      ),
    }));

export const getThreadPartRefinements = (rad) => (thread_id) =>
  (rad.activities ?? [])
    .filter(
      (refinement) =>
        refinement.thread_id === thread_id && refinement.type === "part"
    )
    .map((refinement) => ({
      ...refinement,
      furthestX: mostDistantSubthreadX(
        refinement,
        getSubthreads(rad)(refinement.id)
      ),
    }));

export const getThreadPartRepeats = (rad) => (thread_id) =>
  (rad.activities ?? []).filter(
    (refinement) =>
      refinement.thread_id === thread_id && refinement.type === "repeat"
  );

export const getRoleById = (rad) => (role_id) =>
  (rad.roles ?? []).find((role) => role.id === role_id);

export const getThread = (rad) => (thread_id) =>
  (rad.threads ?? []).find((thread) => thread.id === thread_id);

export const getStateById = (rad) => (state_id) =>
  (rad.states ?? []).find((state) => state.id === state_id);

export const getEntityByIdAndClass = (rad) => (id, className) => {
  switch (className) {
    case "role":
      return (rad.roles ?? []).find((role) => role.id === id);
    case "thread":
    case "case":
    case "part":
    case "repeat":
      return (rad.threads ?? []).find((thread) => thread.id === id);
    case "activity":
    case "refinement":
      return (rad.activities ?? []).find((activity) => activity.id === id);
    case "state": {
      const radstate = (rad.states ?? []).find((state) => state.id === id);
      return radstate;
    }
    case "connection":
      return (rad.connections ?? []).find((connection) => connection.id === id);
    default:
      return []; // safe default
  }
};

export const getSubthreads = (rad) => (id) => {
  return (rad.threads ?? []).filter((c) => c.parent_id === id);
};

export const getThreadByState = (rad) => (stateId) => {
  const activity = rad.activities.find(
    (activity) => activity.prestate_id === stateId
  );
  if (activity) {
    return rad.threads.find((thread) => thread.id === activity.thread_id);
  } else {
    const activityPost = rad.activities.find(
      (activity) => activity.poststate_id === stateId
    );
    if (activityPost) {
      return rad.threads.find((thread) => thread.id === activityPost.thread_id);
    }
  }
  return null;
};

export const addPrecedingPositions = (threads) => {
  // Sort threads by their index in ascending order
  const sortedThreads = [...threads].sort((a, b) => a.index - b.index);

  // Create a map of threads by their index for quick lookup
  const threadMap = new Map(
    sortedThreads.map((thread) => [thread.index, thread])
  );

  // Iterate through the sorted threads and add the preceding position
  return sortedThreads.map((thread) => {
    const precedingThread = threadMap.get(thread.index - 1); // Find the preceding thread
    return {
      ...thread,
      precedingPosition: precedingThread
        ? { x: precedingThread.position.x, y: precedingThread.position.y }
        : null, // If no preceding thread, set to null
    };
  });
};

export const getThreadSequence = (rad) => (thread_id, prestate_id) => {
  const { threads } = rad;
  const thread = threads.find((t) => t.id === thread_id);
  if (!thread) return [];

  if (!prestate_id) {
    prestate_id = thread.prestate_id;
  }

  // Collect all entities for the thread
  const entities = [...getThreadActivities(rad)(thread_id)];

  if (!entities.length) return [];

  // Create a lookup map to quickly get an entity by its prestate
  const entityMap = new Map(
    entities.map((entity) => [entity.prestate_id, entity])
  );

  // Build the sequence based on prestate -> poststate chaining
  const sequence = [];
  let currentPrestate = prestate_id;
  let visited = new Set(); // Prevent infinite loops

  // Handle null prestate (for subthreads) - find the first activity
  // with null prestate
  if (currentPrestate === null || currentPrestate === undefined) {
    const firstEntity = entities.find(
      (entity) =>
        entity.prestate_id === null || entity.prestate_id === undefined
    );
    if (firstEntity) {
      sequence.push(firstEntity);
      currentPrestate = firstEntity.poststate_id;
    }
  }

  while (currentPrestate && entityMap.has(currentPrestate)) {
    if (visited.has(currentPrestate)) break; // Avoid circular references
    visited.add(currentPrestate);

    const entity = entityMap.get(currentPrestate);
    sequence.push(entity);
    currentPrestate = entity.poststate_id;
  }

  return sequence;
};

export const getThreadSequenceReverse = (rad) => (thread_id) => {
  // getThreadSequence then flip the order
  const sequence = getThreadSequence(rad)(thread_id);
  return sequence.slice().reverse();
};

export const getActivitiesForward = (rad) => (thread_id, state_id) => {
  const sequence = getThreadSequence(rad)(thread_id);
  // clip off activities before the one with a prestate matching state_id
  const clipped = [];
  let found = false;
  for (const activity of sequence) {
    if (activity.prestate_id === state_id) {
      found = true;
    }
    if (found) {
      clipped.push(activity);
    }
  }
  return clipped;
};

const getActivitiesReverse = (rad) => (thread_id, state_id) => {
  const sequence = getThreadSequence(rad)(thread_id);
  // clip off activities after the one with a poststate matching state_id
  const clipped = [];
  for (const activity of sequence) {
    clipped.push(activity);
    if (activity.poststate_id === state_id) {
      break;
    }
  }
  // Return the clipped list in reverse order
  return clipped.reverse();
};

export const getTerminalActivity = (rad) => (thread_id) => {
  const thread = getThread(rad)(thread_id);
  if (!thread) return null;

  const activities = getThreadSequence(rad)(thread_id, thread.prestate_id);
  if (!activities.length) return null;

  return activities[activities.length - 1];
};

export const getTerminalState = (rad) => (thread_id) => {
  return getTerminalActivity(rad)(thread_id)?.poststate_id;
};

// Check if indices are within bounds
export const areIndicesValid = (subthreads, fromIndex, toIndex) => {
  return (
    fromIndex >= 0 &&
    toIndex >= 0 &&
    fromIndex < subthreads.length &&
    toIndex < subthreads.length
  );
};

export const getActivity = (rad) => (activity_id) =>
  rad.activities.find((activity) => activity.id === activity_id);

export const getActivityByPrestate = (rad) => (prestate_id) =>
  rad.activities.find((activity) => activity.prestate_id === prestate_id);

export const getActivityByPoststate = (rad) => (poststate_id) => {
  const activities = rad.activities.filter(
    (activity) => activity.poststate_id === poststate_id
  );
  const result =
    activities.length > 0
      ? activities.find((activity) => {
          // If multiple activities share the same
          // poststate, return only the one activity
          // whose thread is not merged
          const thread = getThread(rad)(activity.thread_id);
          return thread && thread.status !== "merged";
        }) || null
      : null;
  return result;
};

export const getParentRole = (rad) => (thread_id) => {
  const thread = getThread(rad)(thread_id);

  // Handle null/undefined thread
  if (!thread) return null;

  // If this is a top-level thread, return the role object
  if (thread.class === "thread") {
    return getRoleById(rad)(thread.role_id);
  }

  // For subthreads (case, part, repeat), recursively find parent
  if (thread.parent_id) {
    return getParentRole(rad)(getActivity(rad)(thread.parent_id).thread_id);
  }

  // Fallback: no parent found
  return null;
};

export const getConnections = (rad) => {
  return rad.connections ?? [];
};

export const getConnectionByInteraction = (rad) => (interaction_id) => {
  return rad.connections.find((con) =>
    con.interactions.some((id) => id === interaction_id)
  );
};

export const getConnectedInteractions = (rad) => {
  if (!rad.connections) {
    return [];
  }
  // for each connection group, replace the array of interaction ids
  // with an array of the actual interaction objects
  const c = rad.connections.map((con) => ({
    ...con,
    interactions: con.interactions.map((id) => getActivity(rad)(id)),
  }));
  return c;
};

export const getRoleStates = (rad) => (role_id) => {
  const roleThreads = (rad.threads || []).filter(
    (thread) => thread.role_id === role_id
  );
  const states = [];
  roleThreads.forEach((thread) => {
    // get the activities in the thread in the correct order
    const activities = getThreadActivities(rad)(thread.id);
    const threadStates = activities
      .map((a) => [a.prestate_id, a.poststate_id])
      .flat()
      .filter((s) => s !== null && s !== undefined);
    states.push(...threadStates);
  });
  return Array.from(new Set(states));
};

export const getAncestorStates = (rad) => (thread_id) => {
  // When the user selects the loop thread option in the component editor,
  // we need to find all ancestor states that can be loop targets.
  // Each subthread has a parent refinement, which has a parent thread.
  // We traverse up the tree until we reach the top-level thread.
  const ancestorStates = [];
  const currentThread = getThread(rad)(thread_id);
  if (!currentThread) return ancestorStates;
  //
  let threadStates = getThreadSequence(rad)(thread_id, null).map(
    (a) => a.prestate_id
  );
  ancestorStates.push(...threadStates);
  //

  let parentActivity = rad.activities.find(
    (a) => a.id === currentThread.parent_id
  );
  while (parentActivity) {
    ancestorStates.push(parentActivity.prestate_id); // include the refinement's prestate as subthreads have a null prestate
    const parentThread = getThread(rad)(parentActivity.thread_id);
    if (!parentThread) break;
    // Get activities that are prior to and including the parent refinement activity
    const acts = getActivitiesReverse(rad)(
      parentThread.id,
      parentActivity.prestate_id
    );
    threadStates = acts.map((a) => a.prestate_id);
    ancestorStates.push(...threadStates);
    // Move up the tree
    parentActivity = rad.activities.find(
      (a) => a.id === parentThread.parent_id
    );
  }
  // Remove duplicates and null/undefined states
  const uniqueStates = Array.from(new Set(ancestorStates)).filter(
    (s) => s !== null && s !== undefined
  );
  return uniqueStates;
};

export const getMergeStates = (rad) => (thread) => {
  // When the user selects the merge states option in the component editor,
  // we need to find all states that can be merge targets for the provided thread.
  // We take an opinion here and select states at the top of threads in the current
  // role up until the the first interaction or refinement activity in each thread.
  // The thread that contains the source state is excluded from the targets.
  const mergeStates = [];
  //debugUtils("getMergeStates - thread", thread);
  const currentThread = getThread(rad)(thread.id);
  //debugUtils("getMergeStates - currentThread", currentThread);
  if (!currentThread) return mergeStates;
  const parentRole = getParentRole(rad)(currentThread.id);
  debugUtils("getMergeStates - parentRole", parentRole);
  if (!parentRole) return mergeStates;
  const roleThreads = getRoleThreads(rad)(parentRole.id);
  //debugUtils("getMergeStates - roleThreads", roleThreads);
  roleThreads.forEach((thread) => {
    if (thread.id === currentThread.id) return; // skip current thread
    // Get activities in the thread
    const activities = getThreadSequence(rad)(thread.id, null);
    if (activities.length === 0) return;
    // Every prestate before the first interaction or refinement is a candidate
    for (const activity of activities) {
      mergeStates.push(activity.prestate_id);
      if (["interaction", "refinement"].includes(activity.type)) {
        break;
      }
    }
  });

  return mergeStates;
};

export const getStateAncestors = (rad) => (state_id) => {
  if (!state_id) return [];

  let allInteractionIds = new Set(getAncestorInteractions(rad)(state_id));
  //debugUtils("getStateAncestors - INITIAL", [...allInteractionIds]);

  let hasNewInteractions = true;

  while (hasNewInteractions) {
    // Get all connections that contain any of our current interactions
    const connectedInteractions = [...allInteractionIds]
      .map((ancestor) =>
        getConnections(rad).filter((con) =>
          con.interactions.some((id) => id === ancestor)
        )
      )
      .flat()
      .flatMap((con) => con.interactions)
      .filter((id) => !allInteractionIds.has(id)); // Only new interactions

    if (connectedInteractions.length > 0) {
      //debugUtils("getStateAncestors - ADDING MORE", connectedInteractions);

      // Add the new connected interactions
      connectedInteractions.forEach((id) => allInteractionIds.add(id));

      // Get ancestors for each new interaction and add them
      const moreAncestors = connectedInteractions
        .map((id) => {
          const interaction = getActivity(rad)(id);
          return getAncestorInteractions(rad)(interaction.prestate_id);
        })
        .flat()
        .filter((id) => !allInteractionIds.has(id)); // Only truly new ones

      if (moreAncestors.length > 0) {
        moreAncestors.forEach((id) => allInteractionIds.add(id));
      }

      hasNewInteractions =
        connectedInteractions.length > 0 || moreAncestors.length > 0;
    } else {
      hasNewInteractions = false;
    }
  }

  return processAncestors(rad, [...allInteractionIds]);
};

export const getStateSuccessors = (rad) => (state_id) => {
  if (!state_id) return [];
  let allInteractionIds = new Set(getSuccessorInteractions(rad)(state_id));
  debugUtils("getStateSuccessors - INITIAL", [...allInteractionIds]);

  let hasNewInteractions = true;

  while (hasNewInteractions) {
    // Get all connections that contain any of our current interactions
    const connectedInteractions = [...allInteractionIds]
      .map((successor) =>
        getConnections(rad).filter((con) =>
          con.interactions.some((id) => id === successor)
        )
      )
      .flat()
      .flatMap((con) => con.interactions)
      .filter((id) => !allInteractionIds.has(id)); // Only new interactions

    if (connectedInteractions.length > 0) {
      debugUtils("getStateSuccessors - ADDING MORE", connectedInteractions);

      // Add the new connected interactions
      connectedInteractions.forEach((id) => allInteractionIds.add(id));

      // Get ancestors for each new interaction and add them
      const moreSuccessors = connectedInteractions
        .map((id) => {
          const interaction = getActivity(rad)(id);
          return getSuccessorInteractions(rad)(interaction.prestate_id);
        })
        .flat()
        .filter((id) => !allInteractionIds.has(id)); // Only truly new ones

      if (moreSuccessors.length > 0) {
        moreSuccessors.forEach((id) => allInteractionIds.add(id));
      }

      hasNewInteractions =
        connectedInteractions.length > 0 || moreSuccessors.length > 0;
    } else {
      hasNewInteractions = false;
    }
  }

  return processSuccessors(rad, [...allInteractionIds]);
};

const processAncestors = (rad, ancestors) => {
  // Ancestors is a list of interaction ids from a single path up the tree.
  // We need to find any connections that include these interactions
  // and then find any other interactions in those connections and
  // their ancestor interactions.

  //debugUtils("processAncestors - ANCESTORS", ancestors);
  const ancestorConnections = [
    ...new Set(
      ancestors
        .map((ancestor) =>
          getConnections(rad).filter((con) =>
            con.interactions.some((id) => id === ancestor)
          )
        )
        .flat()
    ),
  ];

  debugUtils("processAncestors - ancestorConnections", ancestorConnections);

  const allAncestorInteractions = ancestorConnections.flatMap(
    (con) => con.interactions
  );
  /* debugUtils(
    "processAncestors - allAncestorInteractions",
    allAncestorInteractions
  ); */

  // Put together all ancestor interactions and states
  const excludedAncestors = [
    ...new Set(
      allAncestorInteractions
        .map((a) => {
          const interaction = getActivity(rad)(a);
          const activities = getActivitiesReverse(rad)(
            interaction.thread_id,
            interaction.poststate_id
          );
          let ids = [];
          activities.map((act) => {
            if (act.type === "interaction") {
              ids.push(act.id);
            }
            //
            ids.push(act.prestate_id); // prestate captures thread start states
          });
          return ids;
        })
        .flat()
        .filter(Boolean)
    ),
  ];
  //debugUtils("processAncestors - excludedAncestors", excludedAncestors);
  return excludedAncestors;
};

const processSuccessors = (rad, successors) => {
  // Successors is a list of interaction ids.
  debugUtils("processSuccessors - SUCCESSORS", successors);
  const successorConnections = [
    ...new Set(
      successors
        .map((successor) =>
          getConnections(rad).filter((con) =>
            con.interactions.some((id) => id === successor)
          )
        )
        .flat()
    ),
  ];
  debugUtils("processSuccessors - successorConnections", successorConnections);

  const allSuccessorInteractions = successorConnections.flatMap(
    (con) => con.interactions
  );
  debugUtils(
    "processSuccessors - allSuccessorInteractions",
    allSuccessorInteractions
  );

  const collectRefinementDescendantIds = (
    refinementId,
    visitedThreadIds = new Set()
  ) => {
    const ids = [];
    const subthreads = getSubthreads(rad)(refinementId);
    subthreads.forEach((subthread) => {
      if (!subthread?.id || visitedThreadIds.has(subthread.id)) {
        return;
      }
      visitedThreadIds.add(subthread.id);
      const activities = getThreadActivities(rad)(subthread.id);
      activities.forEach((activity) => {
        if (activity.type === "interaction") {
          ids.push(activity.id);
        }
        ids.push(activity.prestate_id, activity.poststate_id);
        if (activity.class === "refinement") {
          ids.push(
            ...collectRefinementDescendantIds(activity.id, visitedThreadIds)
          );
        }
      });
    });
    return ids;
  };

  // Put together all successor interactions and states
  const excludedSuccessors = [
    ...new Set(
      allSuccessorInteractions
        .map((a) => {
          const interaction = getActivity(rad)(a);
          const activities = getActivitiesForward(rad)(
            interaction.thread_id,
            interaction.prestate_id
          );
          let ids = [];
          activities.map((act) => {
            if (act.type === "interaction") {
              ids.push(act.id);
            }
            //
            ids.push(act.poststate_id); // poststate captures thread end states
            if (act.class === "refinement") {
              // If a refinement is in successor scope, descendant subthread states
              // are also successor scope and should be excluded from targets.
              ids.push(...collectRefinementDescendantIds(act.id));
            }
          });
          return ids;
        })
        .flat()
        .filter(Boolean)
    ),
  ];
  //debugUtils("processSuccessors - excludedSuccessors", excludedSuccessors);
  return excludedSuccessors;
};

export const getAncestorThreads = (rad) => (thread_id) => {
  // For a given thread find all ancestor threads.
  const ancestors = [];
  let currentThread = getThread(rad)(thread_id);
  while (currentThread && currentThread.class !== "thread") {
    const parentActivity = getActivity(rad)(currentThread.parent_id);
    const parentThread = getThread(rad)(parentActivity.thread_id);
    ancestors.push(parentThread);
    currentThread = parentThread;
  }
  ancestors.push(currentThread); // add the top-level thread
  return ancestors;
};

const getAncestorInteractions = (rad) => (state_id) => {
  // For a given state find all ancestor interactions.
  let sourceActivity = getActivityByPoststate(rad)(state_id);
  if (!sourceActivity) {
    // For subthread entry states there may be no incoming activity in the
    // subthread itself; use the first activity only when it is in a subthread.
    const preActivity = getActivityByPrestate(rad)(state_id);
    if (preActivity) {
      const preThread = getThread(rad)(preActivity.thread_id);
      if (preThread && preThread.class !== "thread") {
        sourceActivity = preActivity;
      }
    }
  }
  if (!sourceActivity) return [];

  const sourceThread = getThread(rad)(sourceActivity.thread_id);
  const sourceMainThread = getMainThread(rad)(sourceThread.id);

  const ancestors = [];
  let departurePoint = null;
  // If in a refinement thread walk up the tree to gather ancestor interactions in the
  // single thread to the point of hitting the main thread refinement activity (the departure point)
  if (sourceThread.class !== "thread") {
    // First get all ancestor interactions in the immediate thread
    const interactions = getActivitiesReverse(rad)(
      sourceThread.id,
      state_id
    ).filter((a) => a.type === "interaction");
    if (interactions.length > 0) {
      ancestors.push(...interactions);
    }

    let parentActivity = getActivity(rad)(sourceThread.parent_id);
    let parentThread = getThread(rad)(parentActivity.thread_id);
    departurePoint = parentActivity;

    while (parentThread.class !== "thread") {
      const interactions = getThreadActivities(rad)(parentThread.id).filter(
        (a) => a.type === "interaction"
      );
      if (interactions.length > 0) {
        ancestors.push(...interactions);
      }
      const parentActivity = getActivity(rad)(parentThread.parent_id);
      parentThread = getThread(rad)(parentActivity.thread_id);
      departurePoint = parentActivity;
    }
  }

  // Now walk down the tree of the main thread to all interactons
  // preceding the either the departure point if it exists or the source interaction

  const endStateId = departurePoint ? departurePoint.prestate_id : state_id;

  const mainThreadActivities = getActivitiesReverse(rad)(
    sourceMainThread.id,
    endStateId
  );

  const mainThreadInteractions = mainThreadActivities.filter(
    (a) => a.type === "interaction"
  );
  if (mainThreadInteractions.length > 0) {
    ancestors.push(...mainThreadInteractions);
  }

  const mainThreadPartRefinements = mainThreadActivities.filter(
    (a) => a.class === "refinement" && ["part", "repeat"].includes(a.type)
  );

  // Include any interactions in part refinement threads.
  // Case refinements are exclusive branches and do not contribute ancestor interactions.
  mainThreadPartRefinements.map((refinement) => {
    const subthreads = getSubthreads(rad)(refinement.id);
    subthreads.forEach((subthread) => {
      const interactions = getThreadActivities(rad)(subthread.id).filter(
        (a) => a.type === "interaction"
      );
      ancestors.push(...interactions);
    });
  });
  debugUtils("getAncestorInteractions - ancestors", state_id, ancestors);
  return ancestors.map((a) => a.id).flat();
};

const getSuccessorInteractions = (rad) => (state_id) => {
  // For a given state, find all successor interactions in the same thread
  // Find the departure point activity and then get all successor interactions
  // from there.

  const sourceActivity = getActivityByPrestate(rad)(state_id);
  if (!sourceActivity) return [];

  debugUtils("getSuccessorInteractions - sourceActivity", sourceActivity);

  const sourceThread = getThread(rad)(sourceActivity.thread_id);
  const sourceMainThread = getMainThread(rad)(sourceThread.id);

  const successors = [];
  let departurePoint = null;

  if (sourceThread.class !== "thread") {
    // First get the successors in the immediate thread
    const interactions = getActivitiesForward(rad)(
      sourceThread.id,
      state_id
    ).filter((a) => a.type === "interaction");
    if (interactions.length > 0) {
      successors.push(...interactions);
    }

    let parentActivity = getActivity(rad)(sourceThread.parent_id);
    let parentThread = getThread(rad)(parentActivity.thread_id);
    debugUtils("getSuccessorInteractions - parent thread", parentThread);
    departurePoint = parentActivity;

    while (parentThread.class !== "thread") {
      const interactions = getThreadActivities(rad)(parentThread.id).filter(
        (a) => a.type === "interaction"
      );
      if (interactions.length > 0) {
        successors.push(...interactions);
      }
      const parentActivity = getActivity(rad)(parentThread.parent_id);
      parentThread = getThread(rad)(parentActivity.thread_id);
      departurePoint = parentActivity;
    }
  }
  // Now walk down the tree of the main thread to all interactons
  // following either the departure point if it exists or the source state
  const startStateId = departurePoint ? departurePoint.poststate_id : state_id;
  debugUtils(
    "getSuccessorInteractions - startStateId",
    startStateId,
    departurePoint
  );

  const mainThreadActivities = getActivitiesForward(rad)(
    sourceMainThread.id,
    startStateId
  );

  const mainThreadInteractions = mainThreadActivities.filter(
    (a) => a.type === "interaction"
  );
  if (mainThreadInteractions.length > 0) {
    successors.push(...mainThreadInteractions);
  }

  const mainThreadPartRefinements = mainThreadActivities.filter(
    (a) => a.class === "refinement" //&& a.type === "part"
  );

  // Include any interactions in refinement threads.
  // Case refinements are included this time though perhaps they shouldn't be?
  mainThreadPartRefinements.map((refinement) => {
    const subthreads = getSubthreads(rad)(refinement.id);
    subthreads.forEach((subthread) => {
      const interactions = getThreadActivities(rad)(subthread.id).filter(
        (a) => a.type === "interaction"
      );
      successors.push(...interactions);
    });
  });
  debugUtils("getSuccessorInteractions - successors", successors);
  return successors.map((a) => a.id).flat();
};

export const getAllActivitiesWithRole = (rad) => {
  const allActivities = rad.activities
    .map((a) => {
      const aParentRole = getParentRole(rad)(a.thread_id);
      return { ...a, parentRoleId: aParentRole?.id };
    })
    .map((a) => {
      return {
        id: a.id,
        type: a.type,
        prestate_id: a.prestate_id,
        poststate_id: a.poststate_id,
        parentRoleId: a.parentRoleId,
      };
    });
  return allActivities;
};

export const getAllPartThreadsWithRole = (rad) => {
  const allThreads = rad.threads
    .filter((t) => ["part", "case"].includes(t.class))
    .map((t) => {
      const tParentRole = getParentRole(rad)(t.id);
      return { ...t, parentRoleId: tParentRole?.id };
    })
    .map((t) => {
      return {
        id: t.id,
        class: t.class,
        parent_id: t.parent_id,
        prestate_id: t.prestate_id,
        poststate_id: t.poststate_id,
        parentRoleId: t.parentRoleId,
      };
    });
  return allThreads;
};

// Get the main thread for a given thread (resolving subthreads recursively)
export const getMainThread = (rad) => (thread_id) => {
  const thread = getThread(rad)(thread_id);
  if (!thread) return null;
  if (thread.class === "thread") {
    return thread;
  }

  return getMainThread(rad)(getActivity(rad)(thread.parent_id).thread_id);
};



